import { useEffect } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTaskSummary } from './use-task-overview';

interface SummarySectionProps {
  taskId: string | undefined;
}

/**
 * AI-generated overview summary. Fires the summary endpoint when the
 * component mounts (server returns the cached blob if one exists, or
 * generates fresh on cache miss). Background fetch — never blocks the
 * rest of the overview tab.
 */
export function SummarySection({ taskId }: SummarySectionProps): React.ReactElement | null {
  const summary = useTaskSummary(taskId);

  // Trigger an initial fetch on mount / id change. Idempotent: server
  // returns cached payload if one exists.
  useEffect(() => {
    if (!taskId) return;
    summary.mutate({ regenerate: false });
    // We deliberately omit `summary` from deps — only re-fire on id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Summary</h3>
          {summary.data?.cached && (
            <span className="text-xs text-muted-foreground">
              · cached {formatRelative(summary.data.generatedAt)}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => summary.mutate({ regenerate: true })}
          disabled={summary.isPending || !taskId}
          aria-label="Refresh summary"
        >
          <RefreshCw className={summary.isPending ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
          {summary.isPending ? 'Generating' : 'Refresh'}
        </Button>
      </div>
      {summary.isPending && !summary.data ? (
        <div className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      ) : summary.isError ? (
        <p className="text-sm text-destructive">
          Couldn't generate summary. Click refresh to try again.
        </p>
      ) : summary.data ? (
        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed text-foreground">
          {summary.data.summary}
        </div>
      ) : null}
    </section>
  );
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
